import React, { createContext, useContext, useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert, { AlertColor } from "@mui/material/Alert";
import { SnackbarCloseReason } from "@mui/material/Snackbar";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";
import { Box, LinearProgress } from "@mui/material";

interface SnackbarProps {
	duration: number;
	severity: AlertColor | undefined;
	message: string;
	description?: string;
	icon?: React.ReactNode; // Optional icon prop
	actionButtons?: Array<React.ReactElement>;
	presistent?: boolean;
}

interface SnackbarContextType {
	openSnackbar: (
		message: string,
		severity?: AlertColor,
		description?: string,
		icon?: React.ReactNode,
		actionButtons?: Array<React.ReactElement>,
		presistent?: boolean
	) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
	undefined
);

const colorMapping: Record<
	AlertColor,
	{ background: string; border: string; headerColor: string }
> = {
	success: {
		background: SemanticColor.SUCCESS_100, // Light green
		border: SemanticColor.SUCCESS,
		headerColor: SemanticColor.SUCCESS_300,
	},
	error: {
		background: SemanticColor.ERROR_100, // Light red
		border: SemanticColor.ERROR,
		headerColor: SemanticColor.ERROR_300,
	},
	warning: {
		background: SemanticColor.WARNING_100, // Light orange
		border: SemanticColor.WARNING,
		headerColor: SemanticColor.WARNING_300,
	},
	info: {
		background: "rgba(33, 150, 243, 0.7)", // Light blue
		border: "blue",
		headerColor: SemanticColor.SUCCESS_300,
	},
};

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [snackbarProps, setSnackbarProps] = useState<SnackbarProps>({
		duration: 6000,
		severity: undefined,
		message: "",
		description: "",
		icon: null,
	});
	const [open, setOpen] = useState(false);

	const openSnackbar = (
		message: string,
		severity?: AlertColor,
		description?: string,
		icon?: React.ReactNode,
		actionButtons?: Array<React.ReactElement>,
		presistent?: boolean
	) => {
		setSnackbarProps({
			duration: 6000,
			severity,
			message,
			description,
			icon,
			actionButtons,
			presistent,
		});
		setOpen(true);
	};

	return (
		<SnackbarContext.Provider value={{ openSnackbar }}>
			{children}
			<SnackbarComponent
				open={open}
				setOpen={setOpen}
				snackbarProps={snackbarProps}
			></SnackbarComponent>
		</SnackbarContext.Provider>
	);
};

const SnackbarComponent: React.FC<{
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	snackbarProps: SnackbarProps;
}> = ({ open, setOpen, snackbarProps }) => {
	const [progress, setProgress] = useState(0);
	const [isHovered, setIsHovered] = useState(false);
	const { background, border, headerColor } =
		colorMapping[snackbarProps.severity || "success"];

	const onClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason
	) => {
		if (reason === "clickaway") {
			return;
		}

		setTimeout(() => {
			setProgress(0); //reset progress
			setOpen(false);
		}, 1);
	};

	useEffect(() => {
		if (open && !snackbarProps.presistent) {
			let timer: NodeJS.Timeout;
			const totalDuration = snackbarProps.duration || 6000;

			const updateProgress = () => {
				if (!isHovered) {
					setProgress((prev) => {
						if (prev >= 100) {
							clearInterval(timer);
							onClose(); // Close when finished
							return 100; // Ensure it's set to 100
						}
						return prev + (100 / totalDuration) * 100; // Increment progress
					});
				}
			};

			timer = setInterval(updateProgress, 100);
			return () => clearInterval(timer);
		}
	}, [open, isHovered, snackbarProps.duration, snackbarProps.presistent]);

	return (
		<Snackbar
			open={open}
			onClose={onClose}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
		>
			<Box
				sx={{ width: "360px", position: "relative" }}
				onMouseEnter={() => setIsHovered(true)} // Set hover state to true
				onMouseLeave={() => setIsHovered(false)} // Set hover state to false
			>
				<Alert
					onClose={onClose}
					severity={snackbarProps.severity}
					variant="outlined"
					sx={{
						backgroundColor: background, // Dynamic background color
						border: "none", // Dynamic border color
						position: "relative",
						color: headerColor,
						boxShadow: "0px 4px 8px #7F1C2E26",
					}}
					icon={false} // Disable the default icon
				>
					<div
						className="snackbar-content typography-body-02 wrap-text"
						style={{ color: headerColor }}
					>
						{snackbarProps.icon}
						<div
							className="typography-bold"
							style={{ color: NeutralColor.NEUTRAL_GREY_900 }}
						>
							{" "}
							{snackbarProps.message}{" "}
						</div>
					</div>
					{snackbarProps.description && (
						<div
							className="snackbar-description"
							style={{ color: NeutralColor.NEUTRAL_GREY_600 }}
						>
							{snackbarProps.description}
						</div>
					)}
					{snackbarProps.actionButtons && (
						<div
							style={{
								display: "flex",
								gap: "5px",
								justifyContent: "flex-end",
							}}
						>
							{snackbarProps.actionButtons.map((node: React.ReactElement) => {
								return React.cloneElement(node, {
									onClick: () => {
										onClose(); // Close the Snackbar
										if (node.props.onClick) {
											node.props.onClick(); // Call the action's onClick if provided
										}
									},
								});
							})}
						</div>
					)}
					{!snackbarProps.presistent && (
						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{
								position: "absolute",
								bottom: 0,
								left: 3,
								right: 0,
								width: "calc(100% - 6px)",
								backgroundColor: background,
								borderBottomLeftRadius: "8px 4px",
								borderBottomRightRadius: "8px 4px",

								"& .MuiLinearProgress-bar": {
									backgroundColor: headerColor,
									borderBottomLeftRadius: "8px 4px",
									borderBottomRightRadius: "8px 4px",
								},
							}}
						/>
					)}
				</Alert>
			</Box>
		</Snackbar>
	);
};

export const useSnackbar = () => {
	const context = useContext(SnackbarContext);
	if (!context) {
		throw new Error("useSnackbar must be used within a SnackbarProvider");
	}
	return context;
};
